import Pagination from "../../components/ui/Pagination";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "./CustomersPage.css";
import { getCustomers } from "../../services/customerService";
import { getCustomerDisplayName } from "../../utils/customerDisplay";
import { Customer } from "../../types/customer";
import { FiEye } from "react-icons/fi";
import {
  PageFilterDropdown,
  PageSearchBar,
  PageToolbar,
} from "../../compoonents/PageToolbar";
import PageLoader from "../../compoonents/PageLoader";

// --- ICON IMPORTS ---
import emptyStateIcon from "../../assets/images/emptycustomers.png";

const CustomersPage: React.FC = () => {
  const navigate = useNavigate();

  // Data State
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState<string>(""); // '' | 'true' | 'false'

  // Fetch Customers
  useEffect(() => {
    const fetchCustomers = async () => {
      setLoading(true);
      setError(null);
      try {
        // We pass searchTerm and filterStatus to the API
        // Even if the backend ignores them, we will ALSO filter locally below
        const response = await getCustomers(
          currentPage,
          searchTerm,
          filterStatus,
        );

        setCustomers(response.data?.customers || []);
        setTotalPages(response.data?.pagination?.totalPages ?? 1);
        setTotalItems(response.data?.pagination?.totalItems ?? 0);
      } catch (err) {
        setError("Failed to fetch customers.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Debounce the API call slightly to avoid too many requests
    const timer = setTimeout(() => {
      fetchCustomers();
    }, 500);

    return () => clearTimeout(timer);
  }, [currentPage, searchTerm, filterStatus]);

  const handleViewDetails = (customerId: string) =>
    navigate(`/customers/${customerId}`);

  const CUSTOMER_FILTER_OPTIONS = [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  const handleFilterChange = (status: string) => {
    setFilterStatus(status);
    setCurrentPage(1);
  };

  // === CLIENT-SIDE FILTERING (FALLBACK) ===
  // This ensures the table updates visually even if the backend returns the full list
  const visibleCustomers = customers.filter((customer) => {
    // 1. Filter by Search Term (Name, Phone, Email)
    const searchLower = searchTerm.toLowerCase();
    const fullName =
      `${customer.firstName || ""} ${customer.lastName || ""}`.toLowerCase();
    const phone = (customer.phoneNumber || "").toLowerCase();
    const email = (customer.email || "").toLowerCase();
    const matchesSearch =
      fullName.includes(searchLower) ||
      phone.includes(searchLower) ||
      email.includes(searchLower);

    // 2. Filter by Status (Active/Inactive)
    let matchesStatus = true;
    if (filterStatus === "true") matchesStatus = customer.isActive === true;
    if (filterStatus === "false") matchesStatus = customer.isActive === false;

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="customers-page">
      {/* Header stays visible even during loading */}
      <header className="page-header">
        <h3 style={{ color: "#174078" }}>All customers</h3>
        <div className="page-controls">
          <PageToolbar>
            <PageFilterDropdown
              value={filterStatus}
              options={CUSTOMER_FILTER_OPTIONS}
              onChange={handleFilterChange}
              allLabel="All customers"
            />
            <PageSearchBar
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search by name, phone, status"
            />
          </PageToolbar>
        </div>
      </header>

      {/* ERROR STATE */}
      {error && <div className="page-error">{error}</div>}

      {/* LOADING STATE OVERLAY */}
      {loading && !error && <PageLoader message="Loading customers..." />}

      {/* DATA TABLE */}
      {!loading && !error && (
        <>
          {visibleCustomers.length === 0 ? (
            <div className="customers-page page--empty">
              {searchTerm || filterStatus ? (
                // Empty because of search filter
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <h3>No results found for "{searchTerm}"</h3>
                </div>
              ) : (
                // Empty because no data at all
                <>
                  <img src={emptyStateIcon} alt="No customers" />
                  <h3>No customer has signed up yet</h3>
                </>
              )}
            </div>
          ) : (
            <div className="table-container">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>User ID</th>
                    <th>Name</th>
                    <th>Phone no.</th>
                    <th>Email</th>
                    <th>Date Created</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCustomers.map((customer) => {
                    const fullName = getCustomerDisplayName(customer);
                    return (
                      <tr key={customer.id}>
                        <td data-label="User ID">
                          {customer.id.substring(0, 8)}...
                        </td>
                        <td data-label="Name">{fullName}</td>
                        <td data-label="Phone no.">
                          {customer.phoneNumber || "N/A"}
                        </td>
                        <td data-label="Email">{customer.email}</td>
                        <td data-label="Date Created">
                          {new Date(customer.createdAt).toLocaleDateString()}
                        </td>
                        <td data-label="Status">
                          <span
                            className={`status-badge status-${customer.isActive ? "active" : "inactive"}`}
                          >
                            {customer.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td data-label="Action">
                          <div className="table-action-buttons">
                            <button
                              type="button"
                              className="table-action-btn"
                              title="View details"
                              aria-label="View details"
                              onClick={() => handleViewDetails(customer.id)}
                            >
                              <FiEye />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* PAGINATION */}
          {visibleCustomers.length > 0 && (
            <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={totalItems} onPageChange={setCurrentPage} loading={loading} />
          )}
        </>
      )}
    </div>
  );
};

export default CustomersPage;
